import { useTranslation } from "react-i18next";

/**
 * [07] Stats. Bold display numerals + tiny mono captions in a four-up
 * grid. Numbers stay numeric here — only the label + caption are
 * translated.
 */

type Row = { value: string; unit?: string; key: "hectares" | "passes" | "triage" | "models" };

const STATS: Row[] = [
  { value: "14,200", unit: "ha",   key: "hectares" },
  { value: "240",    unit: "/sec", key: "passes"   },
  { value: "11",     unit: "s",    key: "triage"   },
  { value: "7",                    key: "models"   },
];

const ACCENTS = [
  "text-[color:var(--color-meadow)]",
  "text-[color:var(--color-water-2)]",
  "text-[color:var(--color-coral-2)]",
  "text-[color:var(--color-saffron)]",
];

export function Stats() {
  const { t } = useTranslation();
  return (
    <section className="relative border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-forest)] text-[color:var(--color-paper)] dark:bg-[color:var(--color-app-bg)] dark:text-[color:var(--color-app-text)]">
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <div className="flex items-baseline gap-4 mb-12 md:mb-16">
          <div className="label-mono opacity-90">
            <span className="bracket text-current opacity-80">
              {t("stats.section.index")}
            </span>
          </div>
          <div className="flex-1 h-px bg-current opacity-25" />
          <div className="text-right">
            <div className="label-mono opacity-90">{t("stats.section.title")}</div>
            <div className="coord-mono opacity-60 mt-0.5">{t("stats.section.kicker")}</div>
          </div>
        </div>

        <h2 className="text-display text-5xl md:text-6xl mb-14 max-w-2xl">
          {t("stats.headline.before")}{" "}
          <span className="text-accent text-accent-saffron">
            {t("stats.headline.accent")}
          </span>{" "}
          {t("stats.headline.after")}
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-current/15 border border-current/20">
          {STATS.map((s, i) => (
            <div key={s.key} className="bg-[color:var(--color-forest)] dark:bg-[color:var(--color-app-bg)] p-6 md:p-8 flex flex-col">
              <div className={`text-display text-5xl md:text-6xl num leading-none ${ACCENTS[i % 4]}`}>
                {s.value}
                {s.unit && (
                  <span className="text-2xl md:text-3xl opacity-70 ml-1 font-normal">{s.unit}</span>
                )}
              </div>
              <div className="mt-5 label-mono opacity-90">
                {t(`stats.rows.${s.key}.label`)}
              </div>
              <div className="mt-2 text-sm opacity-70 leading-snug">
                {t(`stats.rows.${s.key}.caption`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
